// GOLDEN PATH CODE
// This is an example of how to call a Smithy client for a single item
import { useQuery, useSuspenseQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import {
  RedVelvetApi,
  GetEventByIdCommandOutput,
  GetParticipationsByEventAndAttendeesCommandOutput,
} from '@amzn/red-velvet-api';
import { getClient } from '../utils/getClient';
import { useSearchParams } from 'react-router-dom';
import { useFeatures, getEnabledFeatures } from './features';

export type RedVelvetApiMethod = 'getEventById' | 'getParticipationsByEventAndAttendees'; // Add more method names here as needed

export type RedVelvetApiMethodParameters<K extends RedVelvetApiMethod> = Parameters<
  RedVelvetApi[K]
>[0]; // This relies on the client generator being consistent

export type RedVelvetApiMethodOutput<K extends RedVelvetApiMethod> = K extends 'getEventById'
  ? GetEventByIdCommandOutput
  : K extends 'getParticipationsByEventAndAttendees'
    ? GetParticipationsByEventAndAttendeesCommandOutput
    : never;

export async function callClientByMethodName<K extends RedVelvetApiMethod>(
  methodName: RedVelvetApiMethod,
  parameters: RedVelvetApiMethodParameters<K>,
  featureFlags: string[]
): Promise<RedVelvetApiMethodOutput<K>> {
  const client = getClient(featureFlags);
  switch (methodName) {
    case 'getEventById':
      return (await client.getEventById(
        parameters as RedVelvetApiMethodParameters<'getEventById'>
      )) as RedVelvetApiMethodOutput<K>;
    case 'getParticipationsByEventAndAttendees':
      return (await client.getParticipationsByEventAndAttendees(
        parameters as RedVelvetApiMethodParameters<'getParticipationsByEventAndAttendees'>
      )) as RedVelvetApiMethodOutput<K>;
  }
}

export type UseSingleRedVelvetQueryOptions<K extends RedVelvetApiMethod> = {
  enabled?: boolean;
  suspense?: boolean;
  redVelvetQueryKey: [K, RedVelvetApiMethodParameters<K>]; // Forces query key to be [methodName, methodParameters]
};

async function callRedVelvetClient<K extends RedVelvetApiMethod>(
  queryClient: QueryClient,
  options: UseSingleRedVelvetQueryOptions<K>,
  featureFlags: string[]
) {
  const [methodName, methodParameters] = options.redVelvetQueryKey;
  const result: RedVelvetApiMethodOutput<K> = await callClientByMethodName(
    methodName,
    methodParameters,
    featureFlags
  );
  return result;
}

function useSingletonPresets<K extends RedVelvetApiMethod>(
  options: UseSingleRedVelvetQueryOptions<K>
) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  return {
    queryOpts: {
      queryKey: ['RedVelvetApi', ...options.redVelvetQueryKey],
      queryFn: () =>
        callRedVelvetClient(queryClient, options, getEnabledFeatures(features, searchParams)),
    },
    refresh: () => {
      queryClient.resetQueries();
    },
  };
}

export function useSingletonRedVelvetQuery<K extends RedVelvetApiMethod>(
  options: UseSingleRedVelvetQueryOptions<K>
) {
  const { queryOpts, refresh } = useSingletonPresets(options);

  return {
    queryResult: useQuery({ ...queryOpts, enabled: options.enabled }),
    refresh,
  };
}

export function useSuspenseSingletonRedVelvetQuery<K extends RedVelvetApiMethod>(
  options: UseSingleRedVelvetQueryOptions<K>
) {
  const { queryOpts, refresh } = useSingletonPresets(options);

  return {
    queryResult: useSuspenseQuery(queryOpts),
    refresh,
  };
}

export async function callQueryClientSingletonRedVelvetQuery<K extends RedVelvetApiMethod>(
  queryClient: QueryClient,
  options: UseSingleRedVelvetQueryOptions<K>,
  featureFlags: string[]
) {
  return await queryClient.ensureQueryData({
    queryKey: ['RedVelvetApi', ...options.redVelvetQueryKey],
    queryFn: () => callRedVelvetClient(queryClient, options, featureFlags),
  });
}
